import { z } from "zod";
import { BusinessDate, EntityBase, Instant, Note } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";
import { PriceSource } from "./pricing.js";

/**
 * Order contracts (requirements §6, architecture §8, DL-010…026). An order is one (unit, business date, meal);
 * submitting the same triple again edits that order (req §6.9). Every order is approved by a manager (DL-012).
 */

// ---------------------------------------------------------------------------------------------------------------
// Status machine (req §6.2). F3 implements draft → approved and cancel; later phases add the rest.

export const OrderStatus = z.enum([
  "draft",
  "submitted",
  "returned",
  "approved",
  "in-production",
  "ready",
  "assigned",
  "on-route",
  "delivered",
  "delivered-short",
  "failed",
  "cancelled",
]);
export type OrderStatus = z.infer<typeof OrderStatus>;

/** Allowed moves. Manager edits do not change status (DL-014); they are `order.adjusted` in any status. */
export const ORDER_TRANSITIONS: Readonly<Record<OrderStatus, readonly OrderStatus[]>> = {
  draft: ["submitted"],
  submitted: ["returned", "approved", "cancelled"],
  returned: ["submitted", "cancelled"],
  approved: ["in-production", "cancelled"],
  // Cancelling after cooking started is allowed; the manager decides whether it is charged (req §6.8).
  "in-production": ["ready", "cancelled"],
  ready: ["assigned"],
  assigned: ["on-route"],
  "on-route": ["delivered", "delivered-short", "failed"],
  failed: ["assigned"],
  delivered: [],
  "delivered-short": [],
  cancelled: [],
};

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_TRANSITIONS[from].includes(to);
}

/** Where the order came from (req §6.1, §8.4). There is no automatic source: nothing is ever auto-submitted (DL-013). */
export const OrderSource = z.enum(["contact-app", "order-link", "manager", "phone", "paper", "event"]);
export type OrderSource = z.infer<typeof OrderSource>;

// ---------------------------------------------------------------------------------------------------------------
// Soft lock (req §6.4–6.5, DL-010/011). Times are Tehran wall-clock; nothing is ever hard-blocked.

/** Wall-clock time `HH:mm`, 24-hour. */
export const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export type LocalTime = z.infer<typeof LocalTime>;

/** A moment relative to the order's business date: `daysBefore` days earlier, at `time` Tehran time. */
export const RelativeTime = z.object({ daysBefore: z.int().min(0).max(7), time: LocalTime });
export type RelativeTime = z.infer<typeof RelativeTime>;

const minutesBefore = (t: RelativeTime) => {
  const [h, m] = t.time.split(":").map(Number) as [number, number];
  return t.daysBefore * 1440 - (h * 60 + m);
};

/** Per meal, overridable per organization or unit through settings (`orders.window`, architecture §8.1). */
export const OrderWindow = z
  .object({ deadline: RelativeTime, cookLock: RelativeTime })
  .refine((w) => minutesBefore(w.cookLock) <= minutesBefore(w.deadline), {
    message: "cook lock must not be before the deadline",
    path: ["cookLock"],
  });
export type OrderWindow = z.infer<typeof OrderWindow>;

/**
 * open: free edit · late: edit allowed, flagged "late" and the manager is told (normal alert) ·
 * locked: the contact files a change request; the order stays as it is until the manager decides (urgent alert).
 */
export const OrderPhase = z.enum(["open", "late", "locked"]);
export type OrderPhase = z.infer<typeof OrderPhase>;

const TEHRAN = "Asia/Tehran";
const tehranDateOnly = new Intl.DateTimeFormat("en-CA", {
  timeZone: TEHRAN,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The current business date in Tehran (UTC+03:30) as an ISO date string (YYYY-MM-DD). */
export function tehranBusinessDate(now: Date): BusinessDate {
  return tehranDateOnly.format(now);
}

const tehranParts = new Intl.DateTimeFormat("en-US", {
  timeZone: TEHRAN,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

/** Tehran's offset from UTC in minutes at instant `t` (Intl, so a future DST change is handled by the runtime). */
function tehranOffsetMinutes(t: number): number {
  const p = Object.fromEntries(tehranParts.formatToParts(t).map((x) => [x.type, x.value]));
  const asUtc = Date.UTC(
    Number(p["year"]),
    Number(p["month"]) - 1,
    Number(p["day"]),
    Number(p["hour"]),
    Number(p["minute"]),
  );
  return Math.round((asUtc - Math.floor(t / 60_000) * 60_000) / 60_000);
}

/** The UTC instant of `at` for an order on `businessDate`. */
export function windowInstant(businessDate: BusinessDate, at: RelativeTime): Date {
  const [y, mo, d] = businessDate.split("-").map(Number) as [number, number, number];
  const [h, mi] = at.time.split(":").map(Number) as [number, number];
  const wall = Date.UTC(y, mo - 1, d - at.daysBefore, h, mi);
  let utc = wall - tehranOffsetMinutes(wall) * 60_000;
  // Second pass settles the rare case where the offset differs between the wall-clock guess and the real instant.
  utc = wall - tehranOffsetMinutes(utc) * 60_000;
  return new Date(utc);
}

export function orderPhase(now: Date, businessDate: BusinessDate, window: OrderWindow): OrderPhase {
  if (now < windowInstant(businessDate, window.deadline)) return "open";
  if (now < windowInstant(businessDate, window.cookLock)) return "late";
  return "locked";
}

// ---------------------------------------------------------------------------------------------------------------
// Lines and orders

/** Portion counts are whole numbers; an unusual count is a warning, never a block (DL-016). */
export const Quantity = z.int().min(1).max(10_000);

export const OrderLineInput = z.object({
  menuItemId: Id,
  /** Null only for companion items. */
  tierId: Id.nullable(),
  quantity: Quantity,
  /** Per-line note, e.g. "less rice" (DL-017). */
  note: Note.default(""),
  /** An extra line beyond the day's menu (DL-018): contacts need the `addOrderLines` switch. */
  extra: z.boolean().default(false),
});
export type OrderLineInput = z.infer<typeof OrderLineInput>;

/** Price fields on a line (§7.3): applied price, its source and whether it was set automatically or by hand. */
export const LinePrice = z.object({
  unitAmount: Rial.nullable(),
  source: PriceSource,
  state: z.enum(["auto", "manual", "repriced"]),
});
export type LinePrice = z.infer<typeof LinePrice>;

export const OrderLine = EntityBase.extend({
  orderId: Id,
  menuItemId: Id,
  tierId: Id.nullable(),
  orderedQuantity: Quantity,
  /** Filled at delivery (F5); the invoice basis decides which count is billed (DL-035). */
  deliveredQuantity: z.int().nonnegative().nullable(),
  note: Note,
  extra: z.boolean(),
  addedBy: z.enum(["contact", "manager"]),
  price: LinePrice,
});
export type OrderLine = z.infer<typeof OrderLine>;

export const OrderFlags = z.object({
  /** Submitted or edited between the deadline and the cook lock. */
  late: z.boolean(),
  /** A change request is waiting for the manager. */
  openChangeRequest: z.boolean(),
  /** Entered by staff for the unit (phone, paper, manual). */
  enteredByStaff: z.boolean(),
});
export type OrderFlags = z.infer<typeof OrderFlags>;

export const Order = EntityBase.extend({
  orderingUnitId: Id,
  businessDate: BusinessDate,
  mealId: Id,
  status: OrderStatus,
  source: OrderSource,
  flags: OrderFlags,
  /** From the contact, shown to the kitchen (DL-017). */
  contactNote: Note,
  /** From the manager to the head chef, shown on the board (DL-017); never shown to the customer. */
  kitchenNote: Note,
  /** Set when returned (DL-015); cleared on the next submission. */
  returnNote: Note,
  submittedAt: Instant.nullable(),
  approvedAt: Instant.nullable(),
  approvedById: Id.nullable(),
});
export type Order = z.infer<typeof Order>;

// ---------------------------------------------------------------------------------------------------------------
// Commands

/** Contact or staff submits (or re-submits) the order for a (unit, date, meal). Re-submission edits it. */
export const OrderSubmit = z.object({
  /** Client-generated, so the hub and offline devices can create orders (ADR-04). */
  id: Id,
  orderingUnitId: Id,
  businessDate: BusinessDate,
  mealId: Id,
  lines: z.array(OrderLineInput).min(1).max(100),
  contactNote: Note.default(""),
  /** Required when editing an existing order (optimistic locking, ADR-11). */
  version: z.int().nonnegative().optional(),
  /** Staff only: where the order came from. Contacts get `contact-app` or `order-link` from their session. */
  source: OrderSource.optional(),
  /** The contact confirmed an unusual count once (req §6.3); the warning is still recorded. */
  confirmedWarnings: z.boolean().default(false),
});
export type OrderSubmit = z.infer<typeof OrderSubmit>;

/** A count far from the unit's usual one, high or low (DL-016). */
export const QuantityWarning = z.object({
  menuItemId: Id,
  tierId: Id.nullable(),
  entered: z.int(),
  usual: z.int(),
  direction: z.enum(["high", "low"]),
});
export type QuantityWarning = z.infer<typeof QuantityWarning>;

/** Manager edit in any status (DL-014). After approval a reason is required and the kitchen is told. */
export const OrderAdjust = z.object({
  version: z.int().nonnegative(),
  lines: z.array(OrderLineInput).min(1).max(100),
  kitchenNote: Note.optional(),
  reason: Note,
});
export type OrderAdjust = z.infer<typeof OrderAdjust>;

export const OrderApprove = z.object({ version: z.int().nonnegative() });

/** Return to the contact with a note, e.g. "no meat today, please replace" (DL-015). */
export const OrderReturn = z.object({ version: z.int().nonnegative(), note: Note.min(1) });

/**
 * Bulk approval sits behind "more" and needs a second confirmation by typing the count (architecture §8.1).
 * Only orders without exceptions are eligible; the server re-checks.
 */
export const OrderBulkApprove = z
  .object({
    orders: z
      .array(z.object({ id: Id, version: z.int().nonnegative() }))
      .min(1)
      .max(500),
    typedCount: z.int().positive(),
  })
  .refine((b) => b.typedCount === b.orders.length, {
    message: "typed count does not match",
    path: ["typedCount"],
  });
export type OrderBulkApprove = z.infer<typeof OrderBulkApprove>;

export const OrderCancel = z.object({ version: z.int().nonnegative(), reason: Note.min(1) });

/** Manager sets a price by hand on one line (DL-037, DL-038). */
export const LinePriceOverride = z.object({
  version: z.int().nonnegative(),
  unitAmount: Rial,
  reason: Note.min(1),
});

/** Why an order needs a look before approval (req §6.6); orders without any may be bulk-approved. */
export const OrderException = z.enum([
  "late",
  "change-request",
  "unusual-quantity",
  "extra-line",
  "unpriced",
]);
export type OrderException = z.infer<typeof OrderException>;

// ---------------------------------------------------------------------------------------------------------------
// Change requests after the cook lock (DL-011)

export const ChangeRequestStatus = z.enum([
  "pending",
  "accepted",
  "accepted-with-note",
  "countered",
  "rejected",
]);
export type ChangeRequestStatus = z.infer<typeof ChangeRequestStatus>;

export const ChangeRequest = EntityBase.extend({
  orderId: Id,
  requestedById: Id,
  /** The full set of lines the contact wants (not a diff), so the decision is unambiguous. */
  lines: z.array(OrderLineInput).min(1).max(100),
  note: Note,
  status: ChangeRequestStatus,
  decidedById: Id.nullable(),
  decidedAt: Instant.nullable(),
  /** Polite message to the contact (DL-010: never "locked"). */
  decisionNote: Note,
  /** For `countered`: the lines the manager offers instead. */
  counterLines: z.array(OrderLineInput).max(100).nullable(),
});
export type ChangeRequest = z.infer<typeof ChangeRequest>;

export const ChangeRequestCreate = z.object({
  id: Id,
  orderVersion: z.int().nonnegative(),
  lines: z.array(OrderLineInput).min(1).max(100),
  note: Note.default(""),
});
export type ChangeRequestCreate = z.infer<typeof ChangeRequestCreate>;

export const ChangeDecision = z.discriminatedUnion("decision", [
  z.object({ decision: z.literal("accept"), version: z.int().nonnegative(), note: Note.default("") }),
  z.object({ decision: z.literal("accept-with-note"), version: z.int().nonnegative(), note: Note.min(1) }),
  z.object({
    decision: z.literal("counter"),
    version: z.int().nonnegative(),
    lines: z.array(OrderLineInput).min(1).max(100),
    note: Note.min(1),
  }),
  z.object({ decision: z.literal("reject"), version: z.int().nonnegative(), note: Note.min(1) }),
]);
export type ChangeDecision = z.infer<typeof ChangeDecision>;

// ---------------------------------------------------------------------------------------------------------------
// Hidden kitchen upgrade (DL-019, architecture §7.8). Separate table and permission `kitchen.upgrades`; it must
// never appear in any customer-facing schema, API response, invoice, receipt or sheet.

export const KitchenUpgrade = EntityBase.extend({
  orderLineId: Id,
  quantity: Quantity,
  fromTierId: Id,
  toTierId: Id,
  byId: Id,
});
export type KitchenUpgrade = z.infer<typeof KitchenUpgrade>;

export const KitchenUpgradeInput = KitchenUpgrade.pick({ orderLineId: true, quantity: true, toTierId: true });

// ---------------------------------------------------------------------------------------------------------------
// Prefill templates (DL-013, DL-021): the form is prefilled, the contact or manager still submits it.

/** ISO weekday, 1 = Monday … 7 = Sunday (Shamsi week starts on Saturday = 6 in the UI). */
export const IsoWeekday = z.int().min(1).max(7);

export const OrderTemplate = EntityBase.extend({
  orderingUnitId: Id,
  weekday: IsoWeekday,
  mealId: Id,
  lines: z.array(OrderLineInput).max(100),
});
export type OrderTemplate = z.infer<typeof OrderTemplate>;

export const OrderPrefill = z.object({
  /** Which source filled the form; differences from it are coloured in the UI (DL-021). */
  source: z.enum(["template", "same-weekday", "last-order", "none"]),
  lines: z.array(OrderLineInput),
});
export type OrderPrefill = z.infer<typeof OrderPrefill>;

// ---------------------------------------------------------------------------------------------------------------
// Views. The customer view is strict: no kitchen note, no upgrades, no cost; prices only with `seePrices`.

export const CustomerOrderLine = z.strictObject({
  id: Id,
  menuItemId: Id,
  tierId: Id.nullable(),
  orderedQuantity: Quantity,
  deliveredQuantity: z.int().nonnegative().nullable(),
  note: Note,
  extra: z.boolean(),
  /** Present only when the viewer's role has `seePrices` (DL-039); filtered in the query layer. */
  unitAmount: Rial.nullable().optional(),
});
export type CustomerOrderLine = z.infer<typeof CustomerOrderLine>;

export const CustomerOrderView = z.strictObject({
  id: Id,
  version: z.int().nonnegative(),
  orderingUnitId: Id,
  businessDate: BusinessDate,
  mealId: Id,
  status: OrderStatus,
  phase: OrderPhase,
  late: z.boolean(),
  openChangeRequest: z.boolean(),
  contactNote: Note,
  returnNote: Note,
  lines: z.array(CustomerOrderLine),
});
export type CustomerOrderView = z.infer<typeof CustomerOrderView>;

export const StaffOrderView = Order.extend({
  phase: OrderPhase,
  lines: z.array(OrderLine),
  changeRequests: z.array(ChangeRequest),
  /** Who else is reviewing this order right now (req §6.6); presence only, not a lock. */
  reviewingBy: z.array(z.object({ personId: Id, since: Instant })),
});
export type StaffOrderView = z.infer<typeof StaffOrderView>;

// ---------------------------------------------------------------------------------------------------------------
// Domain events (architecture §8.2). Payloads carry ids only; consumers read what they need.

export const ORDER_EVENTS = [
  "order.submitted",
  "order.edited",
  "order.adjusted",
  "order.change-requested",
  "order.change-decided",
  "order.approved",
  "order.returned",
  "order.cancelled",
] as const;
export type OrderEvent = (typeof ORDER_EVENTS)[number];

export const OrderEventPayload = z.object({
  orderId: Id,
  orderingUnitId: Id,
  businessDate: BusinessDate,
  mealId: Id,
  status: OrderStatus,
  /** For `order.adjusted` / `order.cancelled`: whether cooking had started, so the kitchen alert is urgent. */
  urgent: z.boolean(),
});
export type OrderEventPayload = z.infer<typeof OrderEventPayload>;
